import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { commonLocalization } from "../../../../../../../functions/tableLocalization";
import { BookingServiceEditedData, ServiceOrderDto } from "../Domain/BookingServicePrompt.types";
import { BookableEquipmentTableRow } from "./BookableEquipmentRow.types";
import { handleInitialBookablEqup } from "./BookingService-BookableEquipment.functions";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { renderTableItemImage } from "../../../../../../Common/ListTablePartial/ListTablePartial.partial";
import { withSeeditMrtInitialState } from "../../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../../themes/use-seedit-mrt-table-options";

type Props = {
  minHeight?: string;
  editedService: BookingServiceEditedData;
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>;
  orderedService: ServiceOrderDto;
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>;
  isMandatory: boolean;
  isTabSelected: boolean;
};

/**
 * @version 0.1.0
 */
export default function BookingServiceBookableEquipmentTable({
  minHeight = "60vh",
  editedService,
  orderedService,
  setOrderedService,
  isMandatory,
  isTabSelected
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const column = useMemo<MRT_ColumnDef<BookableEquipmentTableRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200
      },
      {
        size: 200,
        accessorKey: "category",
        header: t("Category")
      },
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          renderTableItemImage(
            cell.row.original.picturePath,
            "equipment-item",
            100,
            setShowBiggerImage
          ),
        size: 100
      }
      // {
      //   accessorKey: "qty",
      //   header: t("Qty"),
      //   size: 150,
      //   filterVariant: "range-slider",
      //   muiEditTextFieldProps: ({ row }) => {
      //     return {
      //       type: "number",
      //       onChange: event =>
      //         updQuantityOfBookableEquipments(
      //           row,
      //           Number(event.target.value),
      //           editedData,
      //           setEditedData,
      //           serviceOrder,
      //           setServiceOrder
      //         ),
      //       inputProps: { step: 1, min: 0 },
      //       sx: { width: "150px" }
      //     };
      //   }
      //   // enableEditing: true,
      //   // Cell: ({ row: { original } }) => renderCateringServiceQuantity(original)
      //   // Edit: props =>
      //   //   editPermissionSelect({
      //   //     permRow: props.row.original,
      //   //     remoteCompanies,
      //   //     remoteDepartments,
      //   //     remoteAllUsers
      //   //   })
      // }
    ],
    [editedService, i18n.language]
  );

  const handleRowSelect = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) setOrderedService(prev => ({ ...prev, bookableEquipments: [] }));
    else
      setOrderedService(prev => ({
        ...prev,
        bookableEquipments: selectedRows.map(row => ({
          name: row.original.name,
          id: Number(row.id),
          qty: 0,
          setUpTime: row.original.setUpTime,
          bookingLeadTime: row.original.bookingLeadTime
        }))
      }));
  };

  useEffect(() => {
    if (isTabSelected) {
      // row select once the items are ordered
      handleInitialBookablEqup(
        orderedService,
        editedService.bookableEquipmentData,
        setRowSelection,
        isMandatory
      );
    }
  }, [isTabSelected]);

  useEffect(() => {
    handleRowSelect();
  }, [rowSelection]);
  const seeditMrtOptions = useSeeditMrtTableOptions<BookableEquipmentTableRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    state: { isLoading: !editedService.bookableEquipmentData, rowSelection },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    columns: column ?? [],
    data: editedService.bookableEquipmentData ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    // if it's not orderable, cannot select
    enableRowSelection: row => row.original.orderable ?? true,
    positionToolbarAlertBanner: "none",
    paginationDisplayMode: "pages",
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setRowSelection,
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15, 20, 30], variant: "outlined" },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no equipments")
    }
  });

  return (
    <Box sx={{ minHeight }}>
      <MaterialReactTable table={table} />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </Box>
  );
}
